import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { parseDomain } from '@wix/domains-serverless-interface';
import { DomainSearchError } from '../types';
import { fetchWithFallback } from '@wix/premium-fed-sdk/@apis-infra';
import { getError, getErrorMessage, hasActionError, overrideCapabilityConfigs, shouldThrowConditionalError } from './utils';
import { LOGGER_DOMAINS_SEARCH_SUITE_ENTITY } from '../../../constants';
import { ACTION_CONFIG, capabilityConfigs, getErrorStatus } from './actionConfig';
import { extractResult } from '../utils';
/**
 * Status resolver that determines what actions are available for a domain
 * Based on the domains-serverless-interface aggregations
 */
export class DomainStatusResolver {
  constructor(httpClient, context) {
    _defineProperty(this, "httpClient", void 0);
    _defineProperty(this, "context", void 0);
    _defineProperty(this, "actionConfig", void 0);
    this.httpClient = httpClient;
    this.context = context;
    this.actionConfig = ACTION_CONFIG({
      isSiteLevel: Boolean(context.msid)
    });
  }

  /**
   * Resolve domain status and available actions
   */
  async resolveStatus(_ref) {
    let {
      domainName,
      overrides
    } = _ref;
    const {
      msid,
      externalViewer
    } = this.context;
    if (!domainName) {
      return {
        status: undefined
      };
    }
    const parsedDomain = parseDomain(domainName);
    if (parsedDomain.error) {
      return {
        status: parsedDomain.error
      };
    }
    try {
      const {
        configs,
        accountParams
      } = await this._getCapabilites({
        domainName,
        overrides
      });
      const status = this.determineActionWithErrorHandling(configs, parsedDomain);
      const errorStatus = getErrorStatus({
        configs,
        accountParams,
        domain: domainName,
        externalViewer,
        status
      });
      if (errorStatus) {
        return {
          status: errorStatus
        };
      }

      // A submitted domain that resolved to no action and no error surfaces as
      // the first-class NO_STATUS (default renders nothing; consumers override
      // it like any status). Empty input / technical errors stay undefined.
      return {
        status: status ?? DomainSearchError.NO_STATUS
      };
    } catch (error) {
      DomainsApiClient.query.domainsLogger({
        httpClient: this.httpClient,
        request: {
          error: error,
          metadata: {
            domain: domainName,
            msid
          },
          type: 'error',
          entity: LOGGER_DOMAINS_SEARCH_SUITE_ENTITY
        }
      });
      return {
        status: undefined,
        errors: {
          domain: 'Error checking domain status'
        }
      };
    }
  }
  determineActionWithErrorHandling(configs, parsedDomain) {
    // Get actions sorted by priority from ACTION_CONFIG
    const sortedActions = Object.keys(configs).filter(action => {
      var _configs$action, _this$actionConfig;
      return ((_configs$action = configs[action]) == null ? void 0 : _configs$action.capability) && ((_this$actionConfig = this.actionConfig) == null || (_this$actionConfig = _this$actionConfig[action]) == null || _this$actionConfig.businessConditions == null ? void 0 : _this$actionConfig.businessConditions(parsedDomain));
    }).sort((a, b) => this.actionConfig[a].priority - this.actionConfig[b].priority);

    // Find the first available action and handle errors according to rules
    for (const action of sortedActions) {
      if (configs[action]) {
        const actionConfig = configs[action];
        const hasError = (actionConfig == null ? void 0 : actionConfig.promiseResult) && hasActionError(actionConfig.promiseResult);
        if (hasError) {
          const shouldThrowError = this.shouldThrowError(action, configs, this.actionConfig);
          if (shouldThrowError) {
            const errorMessage = getErrorMessage(actionConfig.promiseResult);
            DomainsApiClient.query.domainsLogger({
              httpClient: this.httpClient,
              request: {
                error: getError(actionConfig.promiseResult),
                type: 'error',
                entity: LOGGER_DOMAINS_SEARCH_SUITE_ENTITY
              }
            });
            throw new Error(`${action} failed: ${errorMessage}`);
          }
        }
        return action;
      }
    }
    return undefined;
  }
  async _getCapabilites(_ref2) {
    let {
      domainName,
      overrides
    } = _ref2;
    const {
      asciiDomain
    } = parseDomain(domainName);
    const {
      canConnectOrBindDomainSuite,
      canPurchaseDomainSuite,
      canTransferInDomain,
      domainsAccount,
      canChangeDomainOwner,
      getIsOnSale,
      domainTlds
    } = DomainsApiClient.queries({
      httpClient: this.httpClient
    });
    const actionsResultsReq = !overrides ? [canConnectOrBindDomainSuite({
      domain: domainName,
      msid: this.context.msid ?? ''
    }), canPurchaseDomainSuite({
      domain: domainName
    }), canTransferInDomain({
      domain: domainName
    }), fetchWithFallback(canChangeDomainOwner({
      domain: asciiDomain
    }), {
      canChangeOwner: false
    }), fetchWithFallback(domainTlds({}, {
      staleTime: Infinity,
      cacheTime: Infinity
    }), {
      tlds: []
    }), fetchWithFallback(getIsOnSale({}), {
      isOnSale: false
    })] : [];
    const [domainsAccountResult, ...actionsResults] = await Promise.allSettled([domainsAccount({}), ...actionsResultsReq]);
    const [connectOrBindResult, purchaseResult, transferResult] = actionsResults;
    const scopedDomainsAccountResult = this.scopeAccountToContextSite(domainsAccountResult);
    const overrideConfigs = overrideCapabilityConfigs(overrides);
    // Determine available actions based on capabilities and handle errors
    const configs = capabilityConfigs({
      ...overrideConfigs,
      actionConfigs: this.actionConfig,
      accountParamsResult: scopedDomainsAccountResult,
      ...(connectOrBindResult && {
        connectOrBindResult
      }),
      ...(purchaseResult && {
        purchaseResult
      }),
      ...(transferResult && {
        transferResult
      })
    });
    const accountParams = extractResult(scopedDomainsAccountResult);
    return {
      configs,
      accountParams
    };
  }
  scopeAccountToContextSite(result) {
    var _result$value$sites;
    const {
      msid
    } = this.context;
    if (!msid || result.status !== 'fulfilled' || !result.value) {
      return result;
    }
    const site = (_result$value$sites = result.value.sites) == null ? void 0 : _result$value$sites.find(s => s.siteId === msid);
    if (!site) {
      return result;
    }
    return {
      status: 'fulfilled',
      value: {
        ...result.value,
        msid,
        isPremiumSite: Boolean(site.allowedDomain)
      }
    };
  }

  /**
   * Determine if we should throw an error for a given action based on the mapping rules
   */
  shouldThrowError(action, configs, actionConfig) {
    const config = actionConfig[action];
    switch (config == null ? void 0 : config.errorEnforceRule) {
      case 'always':
        return true;
      case 'never':
        return false;
      case 'conditional':
        // For conditional rules, check if all dependency actions didn't fail
        return shouldThrowConditionalError(config.dependencies, configs);
      default:
        return false;
    }
  }
}
//# sourceMappingURL=statusResolver.js.map